import { useState, useEffect, useRef } from 'react';
import { FunnelIcon, XMarkIcon, } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { hasPermission, PERMISSIONS } from '../utils/rbac';

const CallsPage = () => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    ETAStart: '',
    ETAEnd: '',
    ETD: '',
    'Ship Name': '',
    'Cruise Line': '',
    'Port of call': ''
    // 'Call Type': '',
    // 'Call Status': ''
  });
  const [showPopover, setShowPopover] = useState(false);
  const [selectedCall, setSelectedCall] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const filterRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const findNearestETA = (calls) => {
    const today = new Date();
    
    const validCalls = calls.filter(call => {
      const hasDate = !!call.fields['_x0045_TA2'];
      const date = hasDate ? new Date(call.fields['_x0045_TA2']) : null;
      const isFuture = hasDate && date > today;
      return hasDate && isFuture;
    });
    
    if (validCalls.length === 0) {
      // If no future calls, return the most recent past call
      const pastCalls = calls.filter(call => call.fields['_x0045_TA2']).sort((a, b) => 
        new Date(b.fields['_x0045_TA2']) - new Date(a.fields['_x0045_TA2'])
      );
      return pastCalls[0] || null;
    }
    
    return validCalls.reduce((nearest, current) => {
      const currentDate = new Date(current.fields['_x0045_TA2']);
      if (!nearest) return current;

      const nearestDate = new Date(nearest.fields['_x0045_TA2']);
      const currentDiff = Math.abs(currentDate - today);
      const nearestDiff = Math.abs(nearestDate - today);

      return currentDiff < nearestDiff ? current : nearest;
    }, null);
  };

  useEffect(() => {
    setUserRoles(JSON.parse(localStorage.getItem('user'))?.rules || []);

    const fetchCalls = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No token found in local storage');
        }
        const response = await fetch(`${process.env.REACT_APP_CSH_BASE_URL}/api/v1/callsCalendar?callStatus=Confirmed&season=2025 / 26`, {
          headers: {
            'authorization': token
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch calls');
        }
        const data = await response.json();
        setCalls(data.value);
        
        // Scroll to nearest call after data is set
        setTimeout(() => {
          const nearestCall = findNearestETA(data.value);
          if (nearestCall) {
            const rowElement = document.querySelector(`[data-call-id="${nearestCall.fields.id}"]`);
            const tableContainer = document.querySelector('.overflow-x-auto.overflow-y-auto');
            if (rowElement && tableContainer) {
              const rowTop = rowElement.offsetTop;
              const containerHeight = tableContainer.clientHeight;
              const scrollTop = rowTop - (containerHeight / 2);
              tableContainer.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
              });
            }
          }
        }, 500);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCalls();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterRef]);

  // Calculate user permissions at component level
  const userRules = user?.rules || [];
  const hasShorexAccess = hasPermission(userRules, PERMISSIONS.VIEW_SHOREX, { userRoles: userRules }) || 
                         hasPermission(userRules, PERMISSIONS.CREATE_SHOREX, { userRoles: userRules });
  const hasPortTerminalAccess = hasPermission(userRules, PERMISSIONS.VIEW_FORMS, { userRoles: userRules }) || 
                               hasPermission(userRules, PERMISSIONS.CREATE_FORMS, { userRoles: userRules });

  const handleItemClick = (call) => {
    if (!user || !user.rules) {
      return;
    }
    
    // If user only has Port & Terminal access (no Shore Excursions), go directly to forms
    if (!hasShorexAccess && hasPortTerminalAccess) {
      navigate(`/form/${call.fields.id}`);
      return;
    }
    
    // If user has both accesses or only Shore Excursions access, show the options modal
    setSelectedCall(call);
    setShowPopover(true);
  };

  const handleOptionSelect = (option) => {
    if (selectedCall) {
      switch (option) {
        case 'Shorex':
          navigate(`/shorex/${selectedCall.fields.FID}`);
          break;
        case 'Port & Terminal':
          navigate(`/form/${selectedCall.fields.id}`);
          break;
      }
    }
    setShowPopover(false);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500 dark:border-blue-300"></div></div>;
  }

  if (error) {
    return <div className="text-center text-red-500 dark:text-red-400">{error}</div>;
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleFilterChange = (column, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [column]: value
    }));
  };

  const handleDateChange = (column, value) => {
    // Convert the date to ISO format for consistency
    const isoDate = value ? new Date(value).toISOString().split('T')[0] : '';
    setFilters(prevFilters => ({
      ...prevFilters,
      [column]: isoDate
    }));
  };

  const clearFilters = () => {
    setFilters({
      ETAStart: '',
      ETAEnd: '',
      ETD: '',
      'Ship Name': '',
      'Cruise Line': '',
      'Port of call': ''
      // 'Call Type': '',
      // 'Call Status': ''
    });
  };

  const filteredCalls = calls
    .filter(call => {
      return Object.entries(filters).every(([column, filterValue]) => {
        if (!filterValue) return true;
        
        // Handle ETA date range
        if (column === 'ETAStart' || column === 'ETAEnd') {
          const etaDate = call.fields['_x0045_TA2'];
          if (!etaDate) return false;
          
          const eta = new Date(etaDate);
          const filterDate = new Date(filterValue);
          
          if (column === 'ETAStart') {
            return eta >= filterDate;
          } else if (column === 'ETAEnd') {
            return eta <= filterDate;
          }
        }
        
        let cellValue;
        switch (column) {
          case 'ETD':
            cellValue = call.fields['_x0045_TD2'];
            break;
          case 'Ship Name':
            cellValue = call.fields['lookup_x0020_ship'];
            break;
          case 'Cruise Line':
            cellValue = call.fields['lookup_x0020_ship_x003a_Cruise_x'];
            break;
          case 'Port of call':
            cellValue = call.fields['field_8'];
            break;
          // case 'Call Type':
          //   cellValue = call.fields['field_9'];
          //   break;
          // case 'Call Status':
          //   cellValue = call.fields['field_18'];
          //   break;
          default:
            cellValue = call.fields[column.split(' ').join('')];
        }
        return cellValue && cellValue.toLowerCase().includes(filterValue.toLowerCase());
      });
    })
    .sort((a, b) => {
      // Convert ETA strings to Date objects for comparison
      const dateA = new Date(a.fields['_x0045_TA2'] || '');
      const dateB = new Date(b.fields['_x0045_TA2'] || '');
      return dateA - dateB;
    });



  return (
    <div className="h-screen flex flex-col">
      <div className="flex-shrink-0 flex flex-row justify-between items-center mb-4 px-6 sm:px-8 lg:px-12 py-4">
        <h1 className="text-lg sm:text-2xl font-bold dark:text-white">Calls Calendar</h1>
        <button
          onClick={() => setShowFilters(!showFilters)}
          type="button"
          className="flex items-center px-2 py-1 sm:px-4 sm:py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition duration-300 text-xs sm:text-base whitespace-nowrap ml-2"
        >
          <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
          <span className="hidden sm:inline">Filters</span>
          <span className="sm:hidden">Filter</span>
        </button>
      </div>
      {showFilters && (
        <div ref={filterRef} className="absolute z-10 right-6 sm:right-8 lg:right-12 top-16 w-64 text-sm text-gray-500 bg-white border border-gray-200 rounded-lg shadow-sm dark:text-gray-400 dark:border-gray-600 dark:bg-gray-800">
          <div className="flex justify-between px-3 py-2 bg-gray-100 border-b border-gray-200 rounded-t-lg dark:border-gray-600 dark:bg-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white">Filters</h3>
            <button onClick={() => setFilters({})} className="text-xs text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300">Clear All</button>
          </div>
          <div className="px-3 py-2">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">ETA Date Range</label>
              <div className="space-y-2">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Start Date</label>
                  <input
                    type="date"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={filters.ETAStart || ''}
                    onChange={(e) => setFilters(prevFilters => ({
                      ...prevFilters,
                      ETAStart: e.target.value ? new Date(e.target.value).toISOString().split('T')[0] : ''
                    }))}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">End Date</label>
                  <input
                    type="date"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={filters.ETAEnd || ''}
                    onChange={(e) => setFilters(prevFilters => ({
                      ...prevFilters,
                      ETAEnd: e.target.value ? new Date(e.target.value).toISOString().split('T')[0] : ''
                    }))}
                  />
                </div>
              </div>
            </div>
            {['ETD', 'Ship Name', 'Cruise Line', 'Port of call'].map(column => (
              <div key={column} className="mb-2">
                <label htmlFor={column} className="block text-sm font-medium text-gray-700 dark:text-gray-300">{column}</label>
                {column === 'ETD' ? (
                  <input
                    type="date"
                    id={column}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={filters[column] || ''}
                    onChange={(e) => setFilters(prevFilters => ({
                      ...prevFilters,
                      [column]: e.target.value ? new Date(e.target.value).toISOString().split('T')[0] : ''
                    }))}
                  />
                ) : (
                  <select
                    id={column}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={filters[column] || ''}
                    onChange={(e) => setFilters(prevFilters => ({
                      ...prevFilters,
                      [column]: e.target.value
                    }))}
                  >
                    <option value="">All</option>
                    {Array.from(new Set(
                      column === 'Ship Name' ? calls.map(call => call.fields['lookup_x0020_ship']) :
                        column === 'Cruise Line' ? calls.map(call => call.fields['lookup_x0020_ship_x003a_Cruise_x']) :
                          column === 'Port of call' ? calls.map(call => call.fields.field_8) :
                            calls.map(call => call.fields[column])
                    )).map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="flex-1 overflow-x-auto overflow-y-auto px-6 sm:px-8 lg:px-12">
        <table className="min-w-full bg-white dark:bg-gray-800">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Title</th> */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cruise Line</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ship Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Port of Call</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ETA</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">ETD</th>
              {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Days in port</th> */}
              {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Call Type</th> */}
              {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Call Status</th> */}
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Previous Port</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Next Port</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredCalls.map((call) => {
              const isNearest = findNearestETA(filteredCalls)?.fields.id === call.fields.id;
              return (
                <tr
                  key={call.fields.id}
                  data-call-id={call.fields.id}
                  className={`cursor-pointer transition-colors duration-200
                    ${isNearest
                      ? 'bg-blue-200 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  onClick={() => handleItemClick(call)}
                >
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {call.fields._x0045_TA2
                      ? new Date(call.fields._x0045_TA2).toISOString().slice(0, 10)
                      : 'N/A'
                    }
                    {' ' + call.fields.field_8 + ' - ' + call.fields.lookup_x0020_ship_x003a_Cruise_x + ' - ' + call.fields.lookup_x0020_ship}
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{call.fields.id || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{call.fields['lookup_x0020_ship_x003a_Cruise_x'] || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{call.fields['lookup_x0020_ship'] || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {call.fields.field_8 || 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{call.fields['_x0045_TA2'] ? formatDate(call.fields['_x0045_TA2']) : 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{call.fields['_x0045_TD2'] ? formatDate(call.fields['_x0045_TD2']) : 'N/A'}</td>
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{call.fields.field_4 || 'N/A'}</td> */}
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                      {call.fields.field_9 || 'N/A'}
                    </span>
                  </td> */}
                  {/* <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                      {call.fields.field_18 || 'N/A'}
                    </span>
                  </td> */}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{call.fields.field_16 || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">{call.fields.field_17 || 'N/A'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showPopover && selectedCall && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white dark:bg-gray-800 p-6 rounded shadow-lg">
            <h3 className="text-lg font-bold mb-4">Select an Option for {selectedCall.fields.Title}</h3>
            <button
              className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mb-2 w-full ${!hasShorexAccess ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => handleOptionSelect('Shorex')}
              disabled={!hasShorexAccess}
            >
              Shorex
            </button>
            <button
              className={`bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded w-full ${!hasPortTerminalAccess ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={() => handleOptionSelect('Port & Terminal')}
              disabled={!hasPortTerminalAccess}
            >
              Port & Terminal
            </button>
            <button
              className="mt-4 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
              onClick={() => setShowPopover(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallsPage;
